import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import useAuthStore from '@/store/authStore';
import { Surface, useSurfaceStore } from '@/store/useSurfaceStore';
import { listDevices } from '@/api/vendo';
import { fetchStores } from '@/api/store';

/**
 * One-shot heuristic that picks a default surface for accounts that have never
 * chosen one yet. Skipped entirely once the surface is persisted in localStorage
 * or once the URL has inferred a surface (both flip `resolved` to true).
 *
 * Heuristic (per dashboard-switch plan): a main vendo wins → wifi; else any
 * store → voucher-store; else wifi (Piso WiFi is the primary product).
 */
export function useAutoDetectSurface() {
   const isAuthed = useAuthStore((s) => s.isAuthenticated);
   const { resolved, setSurface } = useSurfaceStore();
   const shouldFetch = isAuthed && !resolved;

   const { data: devicesData, isError: devicesFailed } = useQuery({
      queryKey: ['vendo', 'devices', 'auto-detect-surface'],
      queryFn: listDevices,
      enabled: shouldFetch,
      staleTime: 60_000,
   });

   const { data: storesData, isError: storesFailed } = useQuery({
      queryKey: ['stores', 'auto-detect-surface'],
      queryFn: fetchStores,
      enabled: shouldFetch,
      staleTime: 60_000,
   });

   // A failed question must never be able to freeze the app. Both calls 403
   // for an unverified account, and while this hook waited for answers that
   // never came, `resolved` stayed false, SurfaceRedirect never navigated, and
   // the user sat on a blank page retrying forever. Fall back to the default
   // surface instead — Piso WiFi is the primary product, and the user can
   // switch surfaces from the sidebar.
   useEffect(() => {
      if (!shouldFetch) return;
      if (!devicesFailed && !storesFailed) return;
      setSurface('wifi');
   }, [shouldFetch, devicesFailed, storesFailed, setSurface]);

   useEffect(() => {
      if (!shouldFetch) return;
      if (!devicesData || !storesData) return;
      const hasMainVendo = devicesData.devices.some((d) => d.parent == null);
      const hasStore = (storesData.result?.length ?? 0) > 0;
      const next: Surface = hasMainVendo
         ? 'wifi'
         : hasStore
         ? 'voucher-store'
         : 'wifi';
      setSurface(next);
   }, [shouldFetch, devicesData, storesData, setSurface]);
}
